import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { MessageCircle, Heart, Repeat2, Share } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PostContent } from "@/components/post/PostContent";
import { PostType } from "@/types/post";
import { MoreHorizontal } from "lucide-react";

interface CompactPostProps {
  data: PostType;
  onLike?: (e: React.MouseEvent) => void;
  onRepost?: () => void;
  onShare?: () => void;
  onPostClick?: (post: PostType) => void;
  onProfileClick?: (username: string, profile?: any) => void;
  showRepostHeader?: boolean;
  repostUserName?: string;
}

const CompactPost: React.FC<CompactPostProps> = ({
  data,
  onLike,
  onRepost,
  onShare,
  onPostClick,
  onProfileClick,
  showRepostHeader,
  repostUserName
}) => {
  const router = useRouter();
  const [isLiked, setIsLiked] = useState(false);
  const [likes, setLikes] = useState(0);

  const goToUser = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (onProfileClick && data.user?.username) {
      onProfileClick(data?.user?.username, data.user);
    } else if (data.user?.id) {
      router.push(`/users/${data?.user?.id}`);
    }
  }, [router, data.user, onProfileClick]);

  const goToPost = useCallback(() => {
    if (onPostClick) {
      onPostClick(data);
    } else {
      router.push(`/${data.user?.username || data.username || 'user'}/status/${data.id}`);
    }
  }, [router, data, onPostClick]);

  const handleLike = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsLiked(!isLiked);
    setLikes(isLiked ? likes - 1 : likes + 1);
    onLike?.(event);
  }, [isLiked, likes, onLike]);

  const createdAt = formatDistanceToNowStrict(new Date(data.createdAt));

  if (!data.user) return null;

  const postToShow = data.type === "repost" && data.Post ? data.Post : data;

  return (
    <div
      onClick={goToPost}
      className="border-b border-neutral-800 cursor-pointer hover:bg-neutral-900 transition"
    >
      {showRepostHeader && (
        <div className="flex items-center gap-2 text-sm text-neutral-500 px-4 pt-2">
          <Repeat2 className="w-4 h-4" />
          <span>{repostUserName} reposted</span>
        </div>
      )}
      <div className="p-4">
        <div className="flex flex-row items-start gap-3">
          <Avatar
            className="h-10 w-10 flex-shrink-0 cursor-pointer"
            onClick={goToUser}
          >
            <AvatarImage src={data?.user?.image || undefined} />
            <AvatarFallback>{data?.user?.name[0]}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col flex-1">
            <div className="flex flex-row items-center gap-2">
              <p
                onClick={goToUser}
                className="text-white font-semibold cursor-pointer hover:underline"
              >
                {data?.user?.name}
              </p>
              <span
                onClick={goToUser}
                className="text-neutral-500 cursor-pointer hover:underline hidden md:block"
              >
                @{data?.user?.username}
              </span>
              <span className="text-neutral-500 text-sm">
                {createdAt}
              </span>
              <div className="ml-auto">
                <MoreHorizontal className="w-5 h-5 text-neutral-500" />
              </div>
            </div>
            <PostContent
              content={postToShow.content}
              type={postToShow.type || "post"}
              medias={postToShow.medias}
              postId={postToShow.id}
              hasVoted={false}
              pollResults={{}}
              totalVotes={0}
              onVote={async () => {}}
              locationName={postToShow.locationName}
              routeData={postToShow.routeData}
            />
            {data.type === "quote" && data.Post && data?.Post?.user && (
              <div className="mt-2 p-3 border border-neutral-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={data?.Post?.user.image || undefined} />
                    <AvatarFallback>{data?.Post?.user.name[0]}</AvatarFallback>
                  </Avatar>
                  <span className="font-semibold text-sm">{data?.Post?.user.name}</span>
                  <span className="text-neutral-500 text-sm">@{data?.Post?.user.username}</span>
                </div>
                <PostContent
                  content={data?.Post?.content}
                  type="post"
                  medias={data?.Post?.medias}
                  postId={data?.Post?.id}
                  hasVoted={false}
                  pollResults={{}}
                  totalVotes={0}
                  onVote={async () => {}}
                  locationName={data?.Post?.locationName}
                  routeData={data?.Post?.routeData}
                />
              </div>
            )}
            <div className="flex flex-row items-center mt-3 gap-10">
              <div className="flex flex-row items-center text-neutral-500 gap-2 cursor-pointer transition hover:text-sky-500">
                <MessageCircle className="w-5 h-5" />
                <p>{data.replies?.length || 0}</p>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onRepost?.();
                }}
                className="flex flex-row items-center text-neutral-500 gap-2 cursor-pointer transition hover:text-green-500"
              >
                <Repeat2 className="w-5 h-5" />
                <p>{data.reposts?.length || 0}</p>
              </div>
              <div
                onClick={handleLike}
                className="flex flex-row items-center gap-2 cursor-pointer transition hover:text-red-500"
              >
                <Heart
                  className={`w-5 h-5 ${isLiked ? "fill-current text-red-500" : "text-neutral-500"}`}
                />
                <p className={isLiked ? "text-red-500" : "text-neutral-500"}>
                  {data.likes?.length || 0}
                </p>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onShare?.();
                }}
                className="flex flex-row items-center text-neutral-500 gap-2 cursor-pointer transition hover:text-sky-500"
              >
                <Share className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactPost;